import { toast } from "sonner";

import type { TGoogleSignInSchema } from "@/api/auth";
import type { FormSchema } from "@/lib/schema";
import type { AuthResponse } from "@/types";

import useAuthStore from "@/store/auth";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";

export function useAuthenticateUser(
	authFunction: (
		data: FormSchema | TGoogleSignInSchema,
		type: "sign-in" | "sign-up"
	) => Promise<AuthResponse>
) {
	const navigate = useNavigate();
	const authStoreLogin = useAuthStore(state => state.login);
	const setTokens = useAuthStore(state => state.setTokens);

	return useMutation<
		AuthResponse,
		Error,
		| { data: FormSchema; type: "sign-in" | "sign-up" }
		| { data: TGoogleSignInSchema; type: "sign-in" | "sign-up" }
	>({
		mutationKey: ["auth"],
		mutationFn: ({ data, type }) => authFunction(data, type),
		onSuccess: (data) => {
			authStoreLogin(data.data.user, {
				accessToken: data.data.access_token,
				refreshToken: data.data.refreshToken,
				hash: data.data.hash ?? ""
			});
			setTokens({
				accessToken: data.data.access_token,
				refreshToken: data.data.refreshToken
			});
		},
		onError: (error) => {
			import.meta.env.DEV && console.error("Authentication Error:", error);
			if ((error as any).name === "AxiosError") {
				error.message = (error as any).response?.data.message;
				toast.error(error?.message ?? "An error occurred");
				if (error.message === "Email verification required") {
					return navigate({ to: "/verify-email" });
				}
			}
			return error;
		}
	});
}
